/**
 * Geo Location Popup
 * Custom element with Country + Language selects.
 * Auto-shows on country mismatch, manual trigger via .btn-geo-location-trigger.
 */

class GeoLocationPopup extends HTMLElement {
  // Valid IANA timezones per country (ISO-3166 alpha-2 → tz list). Used to detect
  // misrouting: if the visitor's browser timezone matches none of the timezones for
  // the market Shopify routed them to, they were probably misrouted. Countries with
  // overseas territories (ES, PT, FR) list their additional valid timezones to avoid
  // false positives for residents of those regions. Countries not in this map are
  // skipped (no redirect) — extend as needed.
  static COUNTRY_TIMEZONES = {
    AT: ['Europe/Vienna'],
    BE: ['Europe/Brussels'],
    BG: ['Europe/Sofia'],
    HR: ['Europe/Zagreb'],
    CY: ['Asia/Nicosia', 'Asia/Famagusta'],
    CZ: ['Europe/Prague'],
    DK: ['Europe/Copenhagen'],
    EE: ['Europe/Tallinn'],
    FI: ['Europe/Helsinki'],
    FR: ['Europe/Paris'],
    DE: ['Europe/Berlin', 'Europe/Busingen'],
    GR: ['Europe/Athens'],
    HU: ['Europe/Budapest'],
    IS: ['Atlantic/Reykjavik'],
    IE: ['Europe/Dublin'],
    IT: ['Europe/Rome'],
    LV: ['Europe/Riga'],
    LI: ['Europe/Vaduz'],
    LT: ['Europe/Vilnius'],
    LU: ['Europe/Luxembourg'],
    MT: ['Europe/Malta'],
    NL: ['Europe/Amsterdam'],
    NO: ['Europe/Oslo'],
    PL: ['Europe/Warsaw'],
    PT: ['Europe/Lisbon', 'Atlantic/Madeira', 'Atlantic/Azores'],
    RO: ['Europe/Bucharest'],
    SK: ['Europe/Bratislava'],
    SI: ['Europe/Ljubljana'],
    ES: ['Europe/Madrid', 'Atlantic/Canary', 'Africa/Ceuta'],
    SE: ['Europe/Stockholm'],
    CH: ['Europe/Zurich'],
    GB: ['Europe/London']
  };

  constructor() {
    super();
    this.isOpen = false;
    this.focusableElements = [];
    this.lastFocusedElement = null;
    this.autoShowTimeout = null;
  }

  connectedCallback() {
    this.cacheElements();
    this.bindEvents();

    // In customizer, open/close via section select/deselect events
    if (this.isCustomizer) {
      this.boundSectionSelect = this.handleSectionSelect.bind(this);
      this.boundSectionDeselect = this.handleSectionDeselect.bind(this);
      document.addEventListener('shopify:section:select', this.boundSectionSelect);
      document.addEventListener('shopify:section:deselect', this.boundSectionDeselect);
    } else {
      this.checkAutoShow();
    }
  }

  disconnectedCallback() {
    this.unbindEvents();

    if (this.autoShowTimeout) {
      clearTimeout(this.autoShowTimeout);
      this.autoShowTimeout = null;
    }

    if (this.isCustomizer) {
      document.removeEventListener('shopify:section:select', this.boundSectionSelect);
      document.removeEventListener('shopify:section:deselect', this.boundSectionDeselect);
    }

    if (this.isOpen) {
      document.body.style.overflow = '';
    }
  }

  // ============================================
  // Configuration getters
  // ============================================

  get autoShowEnabled() {
    return this.dataset.autoShow === 'true';
  }

  get showOnce() {
    return this.dataset.showOnce === 'true';
  }

  get delay() {
    return parseInt(this.dataset.delay || '0', 10) * 1000;
  }

  get storage() {
    return this.showOnce ? localStorage : sessionStorage;
  }

  get storageKey() {
    return 'geo-popup-shown';
  }

  get isCustomizer() {
    return this.dataset.customizer === 'true';
  }

  get fallbackCountry() {
    return (this.dataset.fallbackCountry || '').trim().toUpperCase();
  }

  get autoRedirect() {
    return this.dataset.autoRedirect === 'true';
  }

  // Returns true when the visitor's browser timezone isn't among the valid timezones
  // for the country they're currently on — i.e. Shopify routed them to a market they
  // don't actually belong to. If we don't have timezones for the current country, we
  // play it safe and return false (no redirect).
  isLikelyMisrouted(currentCountry) {
    const validTimezones = GeoLocationPopup.COUNTRY_TIMEZONES[currentCountry];
    if (!validTimezones || !validTimezones.length) return false;
    const visitorTz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    return !validTimezones.includes(visitorTz);
  }

  // ============================================
  // DOM caching
  // ============================================

  cacheElements() {
    this.overlay = this.querySelector('[data-overlay]');
    this.dialog = this.querySelector('[role="dialog"]');
    this.closeButtons = this.querySelectorAll('[data-close]');
    this.form = this.querySelector('#geo-localization-form');
    this.countrySelect = this.querySelector('[data-country-selector]');
  }

  // ============================================
  // Event binding
  // ============================================

  bindEvents() {
    this.closeButtons.forEach(btn => {
      btn.addEventListener('click', this.handleClose);
    });

    this.overlay?.addEventListener('click', this.handleClose);

    this.boundHandleKeydown = this.handleKeydown.bind(this);
    document.addEventListener('keydown', this.boundHandleKeydown);

    if (this.form) {
      this.form.addEventListener('submit', this.handleFormSubmit);
    }

    // Event delegation for trigger buttons (.btn-geo-location-trigger)
    this.boundHandleTriggerClick = this.handleTriggerClick.bind(this);
    document.addEventListener('click', this.boundHandleTriggerClick);
  }

  unbindEvents() {
    this.closeButtons.forEach(btn => {
      btn.removeEventListener('click', this.handleClose);
    });

    this.overlay?.removeEventListener('click', this.handleClose);

    document.removeEventListener('keydown', this.boundHandleKeydown);

    if (this.form) {
      this.form.removeEventListener('submit', this.handleFormSubmit);
    }

    document.removeEventListener('click', this.boundHandleTriggerClick);
  }

  // ============================================
  // Event handlers (arrow functions for correct `this`)
  // ============================================

  handleClose = () => {
    this.close();
    if (!this.isCustomizer) {
      this.markAsShown();
    }
  };

  handleKeydown(event) {
    if (!this.isOpen) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.handleClose();
      return;
    }

    if (event.key === 'Tab') {
      this.trapFocus(event);
    }
  }

  handleFormSubmit = (event) => {
    // In customizer, prevent actual submission
    if (this.isCustomizer) {
      event.preventDefault();
      return;
    }

    // If selected country is a blended (external) option, redirect instead of submitting
    const selectedOption = this.countrySelect?.options[this.countrySelect.selectedIndex];
    const externalUrl = selectedOption?.dataset.externalUrl;
    if (externalUrl) {
      event.preventDefault();
      this.markAsShown();
      window.location.href = externalUrl;
      return;
    }

    // Save to storage, then let form submit naturally
    this.markAsShown();
  };

  async handleTriggerClick(event) {
    const trigger = event.target.closest('.btn-geo-location-trigger[aria-controls="geo-location-popup"]');
    if (!trigger) return;

    event.preventDefault();
    event.stopPropagation();

    // If triggered from inside the mobile menu drawer, close it first
    const menuDrawer = trigger.closest('#menu-drawer') || document.querySelector('#menu-drawer');
    if (menuDrawer?.open && typeof menuDrawer.hide === 'function') {
      try {
        await menuDrawer.hide();
      } catch {
        // Still open geo popup even if drawer fails to close
      }
    }

    this.open();
  }

  // ============================================
  // Customizer section select/deselect
  // ============================================

  handleSectionSelect(event) {
    if (event.detail.sectionId === this.closest('.shopify-section')?.id?.replace('shopify-section-', '')) {
      this.open();
    }
  }

  handleSectionDeselect(event) {
    if (event.detail.sectionId === this.closest('.shopify-section')?.id?.replace('shopify-section-', '')) {
      this.close();
    }
  }

  // ============================================
  // Auto-show logic (Shopify's client-side API)
  // ============================================

  async checkAutoShow() {
    if (!this.autoShowEnabled) return;
    if (this.hasBeenShown()) return;

    let detectedCountry = await this.getDetectedCountry();
    const currentCountry = this.dataset.currentCountry;

    // Misrouting fix: if a fallback country is configured and the visitor's browser
    // timezone doesn't match the current market's expected timezone, treat them as
    // misrouted and override detection with the fallback country.
    const isMisrouted = this.fallbackCountry
      && this.fallbackCountry !== currentCountry
      && this.isLikelyMisrouted(currentCountry);
    if (isMisrouted) {
      detectedCountry = this.fallbackCountry;
    }

    // No detection result or already on correct market — do nothing
    if (!detectedCountry || detectedCountry === currentCountry) return;

    // Silent redirect mode: submit the localization form without showing the popup.
    // Only triggers for confirmed misrouting — never silently redirects a visitor
    // whose detection just happens to differ from their current market.
    if (isMisrouted && this.autoRedirect && this.form && this.countrySelect) {
      const targetOption = this.countrySelect.querySelector(`option[value="${detectedCountry}"]`);
      if (targetOption) {
        this.markAsShown();
        this.countrySelect.value = detectedCountry;
        this.form.submit();
        return;
      }
    }

    // Pre-select the detected country in the dropdown (only if user hasn't opened it already).
    // Blended (external) countries have value="" and identify themselves via data-country-code.
    if (this.countrySelect && !this.isOpen) {
      let option = this.countrySelect.querySelector(`option[value="${detectedCountry}"]`);
      if (!option) {
        option = this.countrySelect.querySelector(`option[data-country-code="${detectedCountry}"]`);
      }
      if (option) {
        if (option.value) {
          this.countrySelect.value = option.value;
        } else {
          option.selected = true;
        }
      }
    }

    // Country mismatch — show popup after configured delay
    this.autoShowTimeout = setTimeout(() => {
      this.autoShowTimeout = null;
      if (this.isConnected && !this.hasBeenShown()) {
        this.open();
        this.markAsShown();
      }
    }, this.delay);
  }

  async getDetectedCountry() {
    try {
      const response = await fetch(window.Shopify.routes.root + 'browsing_context_suggestions.json');
      const data = await response.json();
      return data.detected_values?.country?.handle?.toUpperCase() || null;
    } catch {
      return null;
    }
  }

  // ============================================
  // Storage helpers
  // ============================================

  hasBeenShown() {
    return this.storage.getItem(this.storageKey) === 'true';
  }

  markAsShown() {
    this.storage.setItem(this.storageKey, 'true');
  }

  // ============================================
  // Open / Close
  // ============================================

  open() {
    if (this.isOpen) return;

    this.lastFocusedElement = document.activeElement;
    this.isOpen = true;
    this.setAttribute('aria-hidden', 'false');
    this.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    this.toggleTriggerExpanded(true);

    requestAnimationFrame(() => {
      this.updateFocusableElements();
      if (this.focusableElements.length > 0) {
        this.focusableElements[0].focus();
      }
    });

    this.dispatchEvent(new CustomEvent('geo-popup:open'));
  }

  close() {
    if (!this.isOpen) return;

    this.isOpen = false;
    this.classList.remove('is-open');
    document.body.style.overflow = '';
    this.toggleTriggerExpanded(false);

    // Restore focus before hiding from assistive technology
    if (this.lastFocusedElement) {
      this.lastFocusedElement.focus();
    }

    this.setAttribute('aria-hidden', 'true');
    this.dispatchEvent(new CustomEvent('geo-popup:close'));
  }

  toggleTriggerExpanded(expanded) {
    document.querySelectorAll('.btn-geo-location-trigger[aria-controls="geo-location-popup"]').forEach(trigger => {
      trigger.setAttribute('aria-expanded', String(expanded));
    });
  }

  // ============================================
  // Focus management
  // ============================================

  updateFocusableElements() {
    if (!this.dialog) {
      this.focusableElements = [];
      return;
    }

    const selectors = [
      'button:not([disabled])',
      'a[href]',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ];

    this.focusableElements = Array.from(
      this.dialog.querySelectorAll(selectors.join(','))
    ).filter(el => el.offsetParent !== null);
  }

  trapFocus(event) {
    if (this.focusableElements.length === 0) return;

    const firstElement = this.focusableElements[0];
    const lastElement = this.focusableElements[this.focusableElements.length - 1];

    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  }
}

if (!customElements.get('geo-location-popup')) {
  customElements.define('geo-location-popup', GeoLocationPopup);
}
